import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Database, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";

interface HudAprRecord {
  id: number;
  person_id: number;
  agency_id: number;
  reporting_period: string | null;
  data: string | null;
  created_at: string;
}

interface HmisFormProps {
  personId: string;
}

export function HmisForm({ personId }: HmisFormProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"universal" | "program">("universal");
  const [showAprForm, setShowAprForm] = useState(false);

  const { data: aprData, isLoading } = useQuery<{ data: HudAprRecord[] }>({
    queryKey: ["hudApr", personId],
    queryFn: () => api.get<{ data: HudAprRecord[] }>(`/clients/${personId}/hud-apr`),
    enabled: !!personId,
  });

  const createAprMutation = useMutation({
    mutationFn: (body: { reportingPeriod?: string; data?: Record<string, unknown> }) =>
      api.post(`/clients/${personId}/hud-apr`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hudApr", personId] });
      setShowAprForm(false);
    },
  });

  const deleteAprMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${personId}/hud-apr/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hudApr", personId] }),
  });

  const aprRecords = aprData?.data ?? [];

  const tabs = [
    { id: "universal" as const, label: "Universal Data Elements" },
    { id: "program" as const, label: "Program Data / HUD APR" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Database className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-base font-semibold">HMIS Data Entry</h3>
      </div>

      <div className="border-b">
        <nav className="-mb-px flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "universal" && <UniversalDataForm personId={personId} />}

      {activeTab === "program" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">HUD APR Data</h4>
            <Button size="sm" onClick={() => setShowAprForm(true)}>Add APR Record</Button>
          </div>

          {showAprForm && (
            <Card>
              <CardContent className="p-4">
                <AprForm
                  onSubmit={(d) => createAprMutation.mutate(d)}
                  onCancel={() => setShowAprForm(false)}
                  isLoading={createAprMutation.isPending}
                />
              </CardContent>
            </Card>
          )}

          {isLoading && <div className="h-20 animate-pulse rounded-lg border bg-muted" />}

          {!isLoading && aprRecords.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-sm text-muted-foreground">No HUD APR data records.</p>
              </CardContent>
            </Card>
          )}

          {aprRecords.map((record) => {
            let parsed: Record<string, unknown> | null = null;
            try { if (record.data) parsed = JSON.parse(record.data); } catch { /* ignore */ }

            return (
              <Card key={record.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">
                      {record.reporting_period || "No period specified"}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm("Delete this APR record?")) deleteAprMutation.mutate(record.id);
                      }}
                      className="text-destructive"
                    >
                      Delete
                    </Button>
                  </div>
                </CardHeader>
                {parsed && (
                  <CardContent>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {Object.entries(parsed).map(([key, value]) => (
                        <div key={key} className="contents">
                          <dt className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}</dt>
                          <dd className="font-medium">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Universal Data Form (client-level HMIS universal data elements)
// ---------------------------------------------------------------------------

function UniversalDataForm({ personId }: { personId: string }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    ssn: "",
    dob: "",
    gender: "",
    race: "",
    ethnicity: "",
    veteranStatus: "",
    disablingCondition: "",
  });

  const saveMutation = useMutation({
    mutationFn: (body: { recordType: string; data: Record<string, unknown> }) =>
      api.post(`/clients/${personId}/hmis`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hmisRecords", personId] });
    },
  });

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Universal Data Elements (HUD 3.1-3.17)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input label="First Name" value={formData.firstName} onChange={(e) => updateField("firstName", e.target.value)} />
          <Input label="Last Name" value={formData.lastName} onChange={(e) => updateField("lastName", e.target.value)} />
          <Input label="SSN (last 4)" value={formData.ssn} onChange={(e) => updateField("ssn", e.target.value)} maxLength={4} />
          <Input label="Date of Birth" type="date" value={formData.dob} onChange={(e) => updateField("dob", e.target.value)} />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Gender</label>
            <select
              value={formData.gender}
              onChange={(e) => updateField("gender", e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select...</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="no_single_gender">No Single Gender</option>
              <option value="transgender">Transgender</option>
              <option value="questioning">Questioning</option>
              <option value="not_disclosed">Client Doesn't Know/Refused</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Race</label>
            <select
              value={formData.race}
              onChange={(e) => updateField("race", e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select...</option>
              <option value="american_indian">American Indian/Alaska Native</option>
              <option value="asian">Asian</option>
              <option value="black">Black/African American</option>
              <option value="pacific_islander">Native Hawaiian/Pacific Islander</option>
              <option value="white">White</option>
              <option value="multi">Multiple Races</option>
              <option value="not_disclosed">Client Doesn't Know/Refused</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Ethnicity</label>
            <select
              value={formData.ethnicity}
              onChange={(e) => updateField("ethnicity", e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select...</option>
              <option value="non_hispanic">Non-Hispanic/Non-Latino</option>
              <option value="hispanic">Hispanic/Latino</option>
              <option value="not_disclosed">Client Doesn't Know/Refused</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Veteran Status</label>
            <select
              value={formData.veteranStatus}
              onChange={(e) => updateField("veteranStatus", e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="not_disclosed">Client Doesn't Know/Refused</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Disabling Condition</label>
            <select
              value={formData.disablingCondition}
              onChange={(e) => updateField("disablingCondition", e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="not_disclosed">Client Doesn't Know/Refused</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            className="gap-2"
            onClick={() => saveMutation.mutate({ recordType: "universal", data: formData })}
            loading={saveMutation.isPending}
          >
            <Save className="h-4 w-4" />
            Save Universal Data
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// APR Form
// ---------------------------------------------------------------------------

interface AprFormProps {
  onSubmit: (data: { reportingPeriod?: string; data?: Record<string, unknown> }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function AprForm({ onSubmit, onCancel, isLoading }: AprFormProps) {
  const [reportingPeriod, setReportingPeriod] = useState("");
  const [jsonData, setJsonData] = useState("");

  return (
    <div className="space-y-3">
      <Input
        label="Reporting Period"
        value={reportingPeriod}
        onChange={(e) => setReportingPeriod(e.target.value)}
        placeholder="e.g., 2025-10-01 to 2026-09-30"
      />
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">APR Data (JSON)</label>
        <textarea
          value={jsonData}
          onChange={(e) => setJsonData(e.target.value)}
          rows={6}
          placeholder='{"totalPersonsServed": 150, "newAdmissions": 45, ...}'
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => {
            let parsed: Record<string, unknown> | undefined;
            if (jsonData) {
              try { parsed = JSON.parse(jsonData); } catch { parsed = { raw: jsonData }; }
            }
            onSubmit({ reportingPeriod: reportingPeriod || undefined, data: parsed });
          }}
          loading={isLoading}
        >
          Save APR Data
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
